import { useCallback, useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { getThemeHistory } from "../../lib/api";
import type { ClusterHistory } from "../../lib/types";
import { ShareButton } from "./ShareButton";
import type { SocialCardFormat } from "../../lib/socialCard";

// ── Types ─────────────────────────────────────────────────────────────────────

interface TimePoint {
  snapshot_at: string;
  size: number;
}

interface ThemeSeries {
  label: string;
  points: TimePoint[];
  currentSize: number;
  maxSize: number;
}

// Normalized for recharts: one row per unique snapshot_at, keyed by theme label
interface ChartRow {
  snapshot_at: string;
  ts: number; // numeric timestamp for X scale
  [themeLabel: string]: number | string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const THEME_COLORS = ["#DB1270", "#C6F430", "#B4B4BA", "#111113", "#707078"] as const;
const REFRESH_INTERVAL_MS = 30_000;

type TimeWindow = "all" | "1h";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** Normalise a label for keying (case-insensitive, trimmed). */
function normaliseKey(label: string): string {
  return label.trim().toLowerCase();
}

/** Pick the most common casing for a display label from a set of raw labels. */
function mostCommonCasing(labels: string[]): string {
  const freq: Record<string, number> = {};
  for (const l of labels) {
    freq[l] = (freq[l] ?? 0) + 1;
  }
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? labels[0];
}

function aggregateHistory(
  rows: ClusterHistory[],
  window: TimeWindow,
): { series: ThemeSeries[]; totalRuns: number } {
  let filtered = rows;
  if (window === "1h") {
    const cutoff = Date.now() - 60 * 60 * 1000;
    filtered = rows.filter((r) => new Date(r.snapshot_at).getTime() >= cutoff);
  }

  // Count distinct cluster_run_ids for empty-state guard
  const totalRuns = new Set(rows.map((r) => r.cluster_run_id)).size;

  // Group by normalised label
  const byLabel = new Map<string, { rawLabels: string[]; bySnap: Map<string, number> }>();
  for (const row of filtered) {
    const key = normaliseKey(row.label);
    if (!byLabel.has(key)) {
      byLabel.set(key, { rawLabels: [], bySnap: new Map() });
    }
    const entry = byLabel.get(key)!;
    entry.rawLabels.push(row.label);
    const prev = entry.bySnap.get(row.snapshot_at) ?? 0;
    entry.bySnap.set(row.snapshot_at, prev + row.size);
  }

  // Build series
  const seriesAll: ThemeSeries[] = [];
  for (const [, entry] of byLabel) {
    const points: TimePoint[] = Array.from(entry.bySnap.entries())
      .map(([snapshot_at, size]) => ({ snapshot_at, size }))
      .sort((a, b) => a.snapshot_at.localeCompare(b.snapshot_at));
    const maxSize = Math.max(...points.map((p) => p.size));
    const currentSize = points[points.length - 1]?.size ?? 0;
    seriesAll.push({
      label: mostCommonCasing(entry.rawLabels),
      points,
      currentSize,
      maxSize,
    });
  }

  // Top 5 by max size
  const top5 = seriesAll.sort((a, b) => b.maxSize - a.maxSize).slice(0, 5);

  return { series: top5, totalRuns };
}

function buildChartRows(series: ThemeSeries[]): ChartRow[] {
  // Collect all unique snapshot_at values across all series
  const allSnaps = new Set<string>();
  for (const s of series) {
    for (const p of s.points) {
      allSnaps.add(p.snapshot_at);
    }
  }
  const sortedSnaps = Array.from(allSnaps).sort();

  return sortedSnaps.map((snap) => {
    const row: ChartRow = { snapshot_at: snap, ts: new Date(snap).getTime() };
    for (const s of series) {
      const point = s.points.find((p) => p.snapshot_at === snap);
      row[s.label] = point?.size ?? 0;
    }
    return row;
  });
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────

interface TooltipEntry {
  dataKey?: string | number;
  value?: number | string | null;
  color?: string;
}

interface TooltipRenderProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
}

function CustomTooltip({ active, payload, label }: TooltipRenderProps) {
  if (!active || !payload || payload.length === 0) return null;

  const time = typeof label === "number" ? label : Number(label);

  return (
    <div
      className="bg-rc-paper border border-rc-fog rounded-xl shadow-card px-4 py-3 font-sans text-[13px]"
      style={{ minWidth: 180 }}
    >
      <p className="text-rc-slate text-[11px] font-semibold uppercase tracking-[0.1em] mb-2">
        {new Intl.DateTimeFormat("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(time))}
      </p>
      {payload.map((entry) => {
        const value = Number(entry.value ?? 0);
        return (
          <div
            key={String(entry.dataKey)}
            className="flex items-center justify-between gap-4 mb-1 last:mb-0"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: entry.color }}
              />
              <span className="text-rc-charcoal truncate" style={{ maxWidth: 120 }}>
                {String(entry.dataKey)}
              </span>
            </span>
            <span className="font-semibold text-rc-black tabular-nums">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────

interface LegendProps {
  series: ThemeSeries[];
  colors: readonly string[];
}

function ChartLegend({ series, colors }: LegendProps) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
      {series.map((s, i) => (
        <div key={s.label} className="flex items-center gap-1.5 min-w-0">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: colors[i] ?? "#B4B4BA" }}
          />
          <span className="text-[12px] text-rc-charcoal truncate" style={{ maxWidth: 160 }}>
            {s.label}
          </span>
          <span className="text-[12px] font-semibold text-rc-black tabular-nums ml-0.5">
            {s.currentSize}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Social card content ───────────────────────────────────────────────────────

interface ThemeVelocitySocialContentProps {
  series: ThemeSeries[];
  eventTitle: string;
  format: SocialCardFormat;
}

function ThemeVelocitySocialContent({
  series,
  eventTitle,
  format,
}: ThemeVelocitySocialContentProps) {
  const fontSize = format === "landscape" ? 20 : 18;
  const top5 = series.slice(0, 5);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#DB1270",
          marginBottom: 4,
        }}
      >
        Top 5 Themen · {eventTitle}
      </div>
      {top5.map((s, i) => (
        <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: THEME_COLORS[i] ?? "#B4B4BA",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              flex: 1,
              fontSize,
              fontWeight: 500,
              color: "#000000",
              lineHeight: 1.4,
            }}
          >
            {s.label}
          </span>
          <span
            style={{
              fontSize,
              fontWeight: 700,
              color: "#000000",
            }}
          >
            {s.currentSize}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

interface ThemeVelocityProps {
  eventId: string;
  questionIds: string[];
  eventTitle?: string;
}

export function ThemeVelocity({ questionIds, eventTitle = "" }: ThemeVelocityProps) {
  const [allRows, setAllRows] = useState<ClusterHistory[]>([]);
  const [window, setWindow] = useState<TimeWindow>("all");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // Measure the chart container ourselves so we can pass explicit numeric
  // width/height to LineChart. ResponsiveContainer logs a noisy "width(-1)"
  // warning during its initial pre-measurement paint that we can't suppress
  // without owning the measurement.
  //
  // Callback ref (not useEffect): the chart wrapper is rendered conditionally
  // — the empty-state path returns before the wrapper exists, so a one-shot
  // useEffect would observe a null ref and never re-fire. A callback ref runs
  // every time React attaches or detaches the element.
  const [chartSize, setChartSize] = useState<{ w: number; h: number } | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const chartBoxRef = useCallback((el: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setChartSize({ w: rect.width, h: rect.height });
      }
    });
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchHistory() {
      if (questionIds.length === 0) return;
      try {
        const perQuestion = await Promise.all(
          questionIds.map((qId) => getThemeHistory(qId)),
        );
        if (!mountedRef.current) return;
        setAllRows(perQuestion.flat());
      } catch {
        // Keep last good data on transient errors
      }
    }

    fetchHistory();

    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(fetchHistory, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [questionIds]);

  const { series, totalRuns } = aggregateHistory(allRows, window);
  const chartRows = buildChartRows(series);

  // ── Empty state ──────────────────────────────────────────────────────────
  if (totalRuns < 2) {
    return (
      <div className="bg-rc-paper border border-rc-fog rounded-2xl p-6 md:p-8 h-full flex flex-col">
        <div className="text-xl md:text-2xl font-bold text-rc-black mb-5">
          Theme Velocity
        </div>
        <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
          <div className="w-10 h-10 rounded-full bg-rc-fog/60 flex items-center justify-center mb-4">
            <svg
              className="w-5 h-5 text-rc-slate"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden="true"
            >
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
          </div>
          <p className="text-[13px] text-rc-slate leading-[1.55] max-w-[18rem]">
            Theme Velocity erscheint nach dem zweiten Cluster-Lauf.
          </p>
        </div>
      </div>
    );
  }

  // ── Accessibility summary ───────────────────────────────────────────────
  const topSeries = series[0];
  const a11ySummary = topSeries
    ? `Theme-Velocity-Diagramm. Top ${series.length} Themen über die Eventlaufzeit. Größtes Thema: ${topSeries.label}, aktuell ${topSeries.currentSize} Antworten.`
    : "Theme-Velocity-Diagramm. Keine Daten verfügbar.";

  return (
    <div className="bg-rc-paper border border-rc-fog rounded-2xl p-6 md:p-8 w-full">
      {/* Header row */}
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="text-xl md:text-2xl font-bold text-rc-black">
            Theme Velocity
          </div>
          <ShareButton
            label="Theme Velocity teilen"
            title={eventTitle}
            cardContent={(fmt) => (
              <ThemeVelocitySocialContent
                series={series}
                eventTitle={eventTitle}
                format={fmt}
              />
            )}
          />
        </div>
        {/* Time window toggle */}
        <div className="flex items-center gap-1 rounded-lg border border-rc-fog p-0.5 bg-rc-cream">
          <button
            type="button"
            onClick={() => setWindow("all")}
            className={`text-[12px] font-medium px-3 py-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rc-berry focus-visible:ring-offset-1 ${
              window === "all"
                ? "bg-rc-paper text-rc-black shadow-card"
                : "text-rc-slate hover:text-rc-black"
            }`}
          >
            Komplett
          </button>
          <button
            type="button"
            onClick={() => setWindow("1h")}
            className={`text-[12px] font-medium px-3 py-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rc-berry focus-visible:ring-offset-1 ${
              window === "1h"
                ? "bg-rc-paper text-rc-black shadow-card"
                : "text-rc-slate hover:text-rc-black"
            }`}
          >
            Letzte Stunde
          </button>
        </div>
      </div>

      {/* Y-axis caption (replaces the rotated recharts label, which clips on narrow viewports) */}
      <div className="text-[11px] uppercase tracking-[0.08em] text-rc-slate mb-1">
        Antworten
      </div>

      {/* Chart */}
      <div
        ref={chartBoxRef}
        className="h-[280px] md:h-[360px] w-full"
        aria-label={a11ySummary}
        role="img"
      >
        {/* Visually-hidden summary for screen readers */}
        <p className="sr-only">{a11ySummary}</p>

        {chartSize && (
          <LineChart
            data={chartRows}
            width={chartSize.w}
            height={chartSize.h}
            margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
          >
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v: number) =>
                formatTime(new Date(v).toISOString())
              }
              tick={{ fontSize: 11, fill: "#707078", fontFamily: "IBM Plex Sans, sans-serif" }}
              tickLine={false}
              axisLine={{ stroke: "#E2E2E5" }}
              minTickGap={40}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#707078", fontFamily: "IBM Plex Sans, sans-serif" }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              content={CustomTooltip as any}
              cursor={{ stroke: "#E2E2E5", strokeWidth: 1 }}
            />
            {series.map((s, i) => (
              <Line
                key={s.label}
                type="monotone"
                dataKey={s.label}
                stroke={THEME_COLORS[i] ?? "#B4B4BA"}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 0, fill: THEME_COLORS[i] ?? "#B4B4BA" }}
                activeDot={{ r: 5, strokeWidth: 0, fill: THEME_COLORS[i] ?? "#B4B4BA" }}
                isAnimationActive={true}
              />
            ))}
          </LineChart>
        )}
      </div>

      {/* Legend */}
      <ChartLegend series={series} colors={THEME_COLORS} />
    </div>
  );
}
